import { i18n } from "@lingui/core";
import { Trans, useLingui } from "@lingui/react";
import { fetchJson } from "@mobility-item-page/client";
import { Intent } from "@mobility-item-page/utilities/client";
import { Button } from "@warp-ds/elements/react/button";
import { toast } from "@warp-ds/elements/toast";
import { type ReactNode, useCallback, useMemo } from "react";
import { ObjectPosition } from "../../types/objectPosition.ts";
import { useValidDealerItemContext } from "../context/DealerItemProviderContext.tsx";
import { useDealerUIState } from "../context/DealerUIStateContext.tsx";
import { useModals } from "../context/ModalContext.tsx";
import { useTracking } from "../utils/pulseHelper.ts";
import { visibleActionsHelper } from "../utils/visibleActionsHelper.ts";
import { ReserveButton, ReserveCar } from "./ReserveCar.tsx";

type Props = {
  position: ObjectPosition;
};

export const VisibleActions = ({ position }: Props) => {
  const {
    itemData: { hideWriteToSeller, salesPersonContactInfo, active },
    organisationData: { phoneNumber: orgPhoneNumber },
    external: { isMobile, reservationEnabled, isItemReserved, isPreview, callMeBackEnabled },
    resolved: { phoneNumberCallbackUrl },
  } = useValidDealerItemContext();

  if (!active && !isPreview) {
    return null;
  }

  const components: ReactNode[] = [];

  const addComponent = (component: ReactNode) => {
    if (component) components.push(component);
  };

  const resolvedPhoneNumber = salesPersonContactInfo?.phone?.phoneNumberFallback ?? orgPhoneNumber;

  visibleActionsHelper({
    position,
    hideWriteToSeller,
    phoneNumberCallbackUrl,
    orgPhoneNumber: resolvedPhoneNumber,
    reservationEnabled,
    isItemReserved,
    callMeBackEnabled,
  }).forEach((component) => {
    switch (component) {
      case "ReserveCar":
        addComponent(<ReserveCar key="reserve-car" />);
        break;
      case "ReserveButton":
        addComponent(<ReserveButton key="reserve-button" />);
        break;
      case "Contact":
        addComponent(
          <Contact
            key="contact"
            primary={
              (position === ObjectPosition.NonStickyCTA &&
                reservationEnabled &&
                isItemReserved &&
                components.length === 1) ||
              components.length === 0
            }
            position={position}
            isPreview={isPreview}
          />,
        );
        break;
      case "Phonenumber":
        addComponent(
          <Phonenumber
            key="phone"
            isMobile={isMobile}
            orgPhoneNumber={resolvedPhoneNumber}
            primary={
              (position === ObjectPosition.NonStickyCTA &&
                reservationEnabled &&
                isItemReserved &&
                components.length === 1) ||
              components.length === 0
            }
            position={position}
            phoneNumberCallbackUrl={phoneNumberCallbackUrl}
            isPreview={isPreview}
            directCallText={salesPersonContactInfo?.phone?.phoneNumberDirectCallText}
          />,
        );
        break;
      case "CallMeBack":
        addComponent(<CallMeBack key="call-me-back" isPreview={isPreview} />);
        break;
    }
  });

  return <>{components}</>;
};

const Contact = ({
  position = ObjectPosition.NonStickyCTA,
  primary = false,
  isPreview = false,
}: {
  position?: ObjectPosition;
  primary?: boolean;
  isPreview?: boolean;
}) => {
  const { trackContactUs } = useTracking();
  const {
    itemData: { salesPersonContactInfo },
    external: { isLoggedIn },
  } = useValidDealerItemContext();
  const { openContactFlow } = useModals();

  const onClick = () => {
    trackContactUs(position);
    if (!isLoggedIn) {
      const { origin, pathname } = window.location;
      const loginUrl = new URL(`${origin}/auth/login`);
      loginUrl.searchParams.set("redirectUrl", `${origin}${pathname}?contact`);
      loginUrl.searchParams.set("cancelUrl", `${origin}${pathname}`);
      window.location.href = loginUrl.toString();
      return;
    }
    openContactFlow();
  };

  const resolvedText =
    salesPersonContactInfo?.email.sendEmailLinkText && position === ObjectPosition.InfoCard
      ? salesPersonContactInfo.email.sendEmailLinkText
      : i18n.t({ id: "dealer.actions.contact", message: "Write to seller" });
  const fullWidth = position !== ObjectPosition.InfoCard;

  const contactVariant = position === ObjectPosition.InfoCard ? "link" : primary ? "primary" : "secondary";

  if (isPreview) {
    return (
      <Button
        variant={contactVariant}
        data-testid="contact"
        fullWidth={fullWidth}
        className={`whitespace-nowrap ${fullWidth ? "md:min-w-[170px]" : ""}`}
      >
        {resolvedText}
      </Button>
    );
  }

  return (
    <Button
      variant={contactVariant}
      data-testid="contact"
      fullWidth={fullWidth}
      className={`whitespace-nowrap ${fullWidth ? "md:min-w-[170px]" : ""}`}
      onClick={onClick}
    >
      {resolvedText}
    </Button>
  );
};

const CallMeBack = ({ isPreview = false }: { isPreview?: boolean }) => {
  const { openCallMeBackFlow } = useModals();
  const { trackContactUs } = useTracking();

  const onClick = () => {
    trackContactUs(ObjectPosition.CallMeBack);
    openCallMeBackFlow();
  };

  return (
    <Button
      variant="secondary"
      data-testid="call-me-back"
      fullWidth
      className="whitespace-nowrap md:min-w-[170px]"
      onClick={isPreview ? undefined : onClick}
    >
      <Trans id="dealer.actions.callMeBack" message="Call me back" />
    </Button>
  );
};

const Phonenumber = ({
  primary,
  isMobile,
  orgPhoneNumber,
  position = ObjectPosition.NonStickyCTA,
  phoneNumberCallbackUrl,
  isPreview = false,
  directCallText,
}: {
  primary?: boolean;
  isMobile?: boolean;
  orgPhoneNumber?: string;
  position?: ObjectPosition;
  phoneNumberCallbackUrl?: string;
  isPreview?: boolean;
  directCallText?: string;
}) => {
  const { trackPhone, trackShowPhone } = useTracking();
  const {
    hiddenPhone,
    setHiddenPhone,
    phoneNumber,
    setPhoneNumber,
    isLoadingTrackedPhoneNumber,
    setIsLoadingTrackedPhoneNumber,
    hasTrackedPhoneNumberFailed,
    setHasTrackedPhoneNumberFailed,
  } = useDealerUIState();
  const { i18n } = useLingui();
  const errorState = useMemo(
    () => hasTrackedPhoneNumberFailed && !phoneNumber,
    [hasTrackedPhoneNumberFailed, phoneNumber],
  );

  const text = useMemo(() => {
    const showPhoneNumberText = i18n.t({ id: "dealer.actions.phonenumber.show", message: "Show phone number" });
    if (hiddenPhone) {
      return showPhoneNumberText;
    }
    if (phoneNumber) {
      return phoneNumber;
    }
    // If there's no number to show, an error text will be shown
    return showPhoneNumberText;
  }, [hiddenPhone, i18n, phoneNumber]);

  const fetchTrackedPhoneNumber = useCallback(async () => {
    try {
      setIsLoadingTrackedPhoneNumber(true);
      setHasTrackedPhoneNumberFailed(false);

      let phoneNumber: string | undefined;

      if (phoneNumberCallbackUrl) {
        // NOTE: The dev server must run on port 8080 (default port) to avoid CORS issues locally
        const response = await fetchJson<{ phoneNumber?: string }>(phoneNumberCallbackUrl);
        if (response.ok && response.data?.phoneNumber) {
          phoneNumber = response.data.phoneNumber;
          setPhoneNumber(phoneNumber);
          return phoneNumber;
        }
      }

      phoneNumber = orgPhoneNumber;
      setPhoneNumber(phoneNumber);
      setHasTrackedPhoneNumberFailed(true);
      return phoneNumber;
    } catch (error) {
      const phoneNumber = orgPhoneNumber;
      setPhoneNumber(phoneNumber);
      setHasTrackedPhoneNumberFailed(true);
      console.error("Failed to fetch tracked phone number:", error);
      return phoneNumber;
    } finally {
      setIsLoadingTrackedPhoneNumber(false);
    }
  }, [
    phoneNumberCallbackUrl,
    setHasTrackedPhoneNumberFailed,
    setIsLoadingTrackedPhoneNumber,
    setPhoneNumber,
    orgPhoneNumber,
  ]);

  if (!orgPhoneNumber && !phoneNumberCallbackUrl) {
    return null;
  }
  const fullWidth = position !== ObjectPosition.InfoCard;

  const phoneVariant = position === ObjectPosition.InfoCard ? "link" : primary ? "primary" : "secondary";

  if (isPreview) {
    return (
      <div className="w-full">
        <Button
          data-testid="phone-number"
          variant={phoneVariant}
          fullWidth={fullWidth}
          className={`whitespace-nowrap ${fullWidth ? "md:min-w-[170px]" : ""}`}
        >
          <Trans id="dealer.actions.phonenumber.show" message="Show phone number" />
        </Button>
      </div>
    );
  }

  const errorDescription = i18n.t({
    id: "dealer.actions.phonenumber.error.description",
    message: "Something went wrong.",
    comment: "Visible if something went wrong when trying to fetch the number",
  });

  const onDesktopClick = async () => {
    if (hiddenPhone) {
      trackShowPhone(position);
      const phoneNumber = await fetchTrackedPhoneNumber();
      if (phoneNumber) setHiddenPhone(false);
    } else {
      trackPhone(position, Intent.Click);
      copyToClipboard(phoneNumber);
      toast(
        i18n.t({
          id: "dealer.actions.phonenumber.copied",
          message: "Copied",
        }),
      );
    }
  };

  const onMobileClick = async () => {
    let numberToCall = phoneNumber;

    if (!numberToCall) {
      numberToCall = await fetchTrackedPhoneNumber();
    }
    trackPhone(position, Intent.Call);

    // If there's no number to call, an error text will be shown
    if (numberToCall) {
      window.location.href = `tel:${numberToCall}`;
    }
  };

  if (isMobile) {
    return (
      <>
        {position === ObjectPosition.InfoCard && isLoadingTrackedPhoneNumber ? (
          <div className="animate-spinner [--spinner-size:16px] my-4"></div>
        ) : (
          <div className={fullWidth ? "w-full" : ""}>
            <Button
              variant={phoneVariant}
              className={`whitespace-nowrap ${fullWidth ? "md:min-w-[170px]" : ""}`}
              fullWidth={fullWidth}
              onClick={onMobileClick}
              loading={isLoadingTrackedPhoneNumber}
            >
              {position === ObjectPosition.InfoCard && directCallText ? (
                directCallText
              ) : (
                <Trans id="dealer.actions.phonenumber.call" message="Call seller" />
              )}
            </Button>
            {errorState ? <p className="s-text-negative pt-8"> {errorDescription} </p> : null}
          </div>
        )}
      </>
    );
  }

  return (
    <>
      {position === ObjectPosition.InfoCard && isLoadingTrackedPhoneNumber ? (
        <div className="animate-spinner [--spinner-size:16px] my-4"></div>
      ) : (
        <div className={fullWidth ? "w-full" : ""}>
          <Button
            variant={phoneVariant}
            data-testid="phone-number"
            fullWidth={fullWidth}
            className={`whitespace-nowrap ${fullWidth ? "md:min-w-[170px]" : ""}`}
            onClick={onDesktopClick}
            loading={isLoadingTrackedPhoneNumber}
          >
            <span className="flex items-center">{text}</span>
          </Button>
          {errorState ? <p className="s-text-negative pt-8"> {errorDescription} </p> : null}
        </div>
      )}
    </>
  );
};

const copyToClipboard = async (textToCopy: string | undefined) => {
  if (!textToCopy) {
    return;
  }
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(textToCopy);
  } else {
    const textArea = document.createElement("textarea");
    textArea.value = textToCopy;

    textArea.style.position = "absolute";
    textArea.style.left = "-999999px";

    document.body.prepend(textArea);
    textArea.select();

    try {
      document.execCommand("copy");
    } catch (error) {
      console.error(error);
    } finally {
      textArea.remove();
    }
  }
};
